/* eslint-disable import/no-unresolved */
import {
    useMutation, useQuery, useReactiveVar, useLazyQuery,
} from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';

import {
    getUserInfoVar, isAuthenticatedVar, selectValueVar, showReportMenuVar,
    tenantIdVar, activeDataReconMenuVar, showDataReconMenusVar,
    leftNavigationItemsVar, tenantNameVar, moduleIdVar, showCrossReportConfigVar,
    showConfigureDataSourceModalVar, isLoggedInVar,
} from 'Apollo/config';
import { GET_USER_INFO, GET_REGULATORS } from 'Apollo/Queries';
import { UPDATE_USER_LOGIN_TIME, LOGOUT_USER } from 'Apollo/Mutations';
import {
    getAccessToken, isAuthenticated, signOutUser, setProfile, setSelectedTenant,
    setSelectedTenantName, setRole, setAllowedModule,
} from 'views/Auth/AuthHelper';
import { transformUserData } from 'utils/userHelpers';
import { HttpStatusCodeEnum } from '../../../constants/index';
import AppBarComponent from '../components';

const outlineAction = {
    NOACTION: 0,
    CLICK: 1,
    CHANGE: 2,
    BLUR: 3,
    TAB: 4,
};

const listItems = [
    [
        <i key={1} className="appkiticon icon-bar-chart-outline" style={{ fontSize: '1.5rem', color: 'white', top: '100px' }} />,
        <span key={2} className="a-nav-item-text">Data Reconciliation</span>,
    ],
];

const AppBar = () => {
    const isLoggedIn = useReactiveVar(isLoggedInVar);
    const tenantVar = useReactiveVar(tenantIdVar);
    const module = useReactiveVar(moduleIdVar);
    const selectedTenantNameVar = useReactiveVar(tenantNameVar);
    const showReportMenu = useReactiveVar(showReportMenuVar);
    const dataReconActiveMenuOption = useReactiveVar(activeDataReconMenuVar);
    const showDataReconMenus = useReactiveVar(showDataReconMenusVar);
    const showCrossReportConfigMenu = useReactiveVar(showCrossReportConfigVar);
    const history = useHistory();
    const location = useLocation();

    /**
     * Single query to local backend — returns full user data
     * including tenants, modules, role, status, etc.
     */
    const [getUserInfo, {
        client, data = {}, error,
    }] = useLazyQuery(GET_USER_INFO, {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    });

    const [updateUserLoginTime] = useMutation(UPDATE_USER_LOGIN_TIME);

    const [logoutUser, {
        data: logoutUserData,
        error: logoutUserError,
    }] = useMutation(LOGOUT_USER);

    const allRegulators = useQuery(GET_REGULATORS);

    const [hasOutLine, setHasOutLine] = useState(false);
    const [lastAction, setLastAction] = useState(outlineAction.NOACTION);
    const [initials, setInitials] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [tenantName, setTenantName] = useState('');
    const [isDataReady, setIsDataReady] = useState(false);

    /* callback function called when mouse is scrolled down */
    const onMouseDown = () => {
        setHasOutLine(false);
        setLastAction(outlineAction.CLICK);
    };

    /* callback function called when focus is blur */
    const onBlur = () => {
        if (lastAction === outlineAction.CLICK) {
            setHasOutLine(false);
            setLastAction(outlineAction.BLUR);
        } else {
            setHasOutLine(true);
            setLastAction(outlineAction.BLUR);
        }
    };

    const resetStore = () => {
         // Clear Apollo cache and reactive vars FIRST
    client.clearStore();
    isAuthenticatedVar(false);
    getUserInfoVar({});
    leftNavigationItemsVar([]);
    moduleIdVar(null);

    // signOutUser handles the redirect to Azure AD logout
    // Don't call history.push after this — signOutUser does a full page redirect
    signOutUser();
    };

    /* callback function called when user logs out */
const onLogout = () => {
    // Skip the logoutUser mutation — IAM service isn't running
    // Azure AD logout (in signOutUser) handles session termination
    resetStore();
};
    /* callback function called to change data recon menu */
    const onChangeDataReconMenu = name => {
        if (name === 'home') {
            history.push({
                pathname: '/data-recon-home',
                state: { tenantId: tenantVar },
            });
        } else {
            history.push({
                pathname: '/projects',
                state: { tenantId: tenantVar },
            });
        }
    };

    const navigateToLandingPage = () => {
        history.push('/');
        showReportMenuVar(false);
        showCrossReportConfigVar(false);
        leftNavigationItemsVar([]);
        moduleIdVar(null);
    };

    const toggleOrganization = (tenant, name) => {
        tenantIdVar(tenant);
        setTenantName(name);
        setSelectedTenant(tenant);
        setSelectedTenantName(name);
        navigateToLandingPage();
    };

    const onConfigureDataSource = () => {
        showConfigureDataSourceModalVar(true);
    };

    /**
     * On mount — call single query to get full user data.
     * No separate IAM call needed — local backend has everything.
     */
    useEffect(() => {
        getUserInfo();
    }, []);

    /**
     * Process backend user data + regulators.
     * Backend returns full user with iamTenant, userTenants, moduleIds.
     * transformUserData() derives fullName, initials, and provides safe defaults.
     * Also store profile data from the same response.
     */
    useEffect(() => {
        if (!isEmpty(data) && !isEmpty(allRegulators.data)) {
            const rawUser = data.getUser;
            if (!rawUser) return;

            const user = transformUserData(rawUser);

            if (!user) return;

            getUserInfoVar({ getUser: user });

            // ✅ Store profile from the same user data
            setProfile(user);

            tenantIdVar(tenantVar || user.iamTenant?.tenantId);
            setSelectedTenant(user.iamTenant?.tenantId);
            setTenantName(user.iamTenant?.name || '');
            setSelectedTenantName(user.iamTenant?.name || '');
            setRole(user.iamRole);
            setAllowedModule(user.moduleIds);
            setInitials(user.initials);
            setUserInfo(user);
            setIsDataReady(true);

            if (location?.state?.regulationId) {
                const result = allRegulators.data.getRegulators.find(
                    r => r.regulatorId === location.state.regulationId,
                );
                selectValueVar(result);
            }
        }
    }, [data, allRegulators]);

    /* Handle app backend user fetch errors */
    useEffect(() => {
        if (error) {
            console.error('AppBar: User info error:', error.message);
            if (error.networkError?.statusCode === HttpStatusCodeEnum.UNAUTHORIZED) {
                resetStore();
            }
        }
    }, [error]);

    /* When tenantVar changes, update tenantName from userTenants */
    useEffect(() => {
        if (userInfo && tenantVar) {
            const selectedTenant = userInfo.userTenants?.find(
                ut => ut.tenantId === tenantVar,
            );
            setTenantName(selectedTenant?.name || tenantName);
        }
    }, [tenantVar]);

    /* Sync tenantName when reactive var changes externally */
    useEffect(() => {
        setTenantName(selectedTenantNameVar);
    }, [selectedTenantNameVar]);

    /* Update user login time when user logs in */
    useEffect(() => {
        if (isLoggedIn) {
            updateUserLoginTime();
        }
    }, [isLoggedIn]);

    /* Handle successful logout */
    useEffect(() => {
        if (logoutUserData?.logoutUser) {
            resetStore();
        }
    }, [logoutUserData]);

    /* Handle logout errors */
    useEffect(() => {
        if (logoutUserError
            || logoutUserError?.networkError?.statusCode === HttpStatusCodeEnum.UNAUTHORIZED) {
            resetStore();
        }
    }, [logoutUserError]);

    /**
     * Don't render AppBarComponent until data is ready.
     * Prevents crash: "Cannot read properties of null"
     */
    if (!isDataReady) {
        return null;
    }

    return (
        <AppBarComponent
            history={history}
            tenantVar={tenantVar}
            showReportMenu={showReportMenu}
            dataReconActiveMenuOption={dataReconActiveMenuOption}
            showDataReconMenus={showDataReconMenus}
            hasOutLine={hasOutLine}
            onMouseDown={onMouseDown}
            onBlur={onBlur}
            onLogout={onLogout}
            initials={initials}
            onChangeDataReconMenu={onChangeDataReconMenu}
            navigateToLandingPage={navigateToLandingPage}
            leftNavigationItemsVar={leftNavigationItemsVar}
            listItems={listItems}
            userInfo={userInfo}
            toggleOrganization={toggleOrganization}
            tenantName={tenantName}
            module={module}
            showCrossReportConfigMenu={showCrossReportConfigMenu}
            onConfigureDataSource={onConfigureDataSource}
        />
    );
};

export default AppBar;